import { useState } from 'react';
import { AlertTriangle, X, Zap } from 'lucide-react';
import { BillingStatus } from '../src/services/billing.service';

interface Props {
  status: BillingStatus;
  onUpgradeClick: () => void;
}

export function TrialBanner({ status, onUpgradeClick }: Props) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;
  if (status.status === 'active') return null;

  const daysLeft = status.trial_days_remaining ?? 0;
  const isUrgent = daysLeft <= 3;
  const isExpired = status.status === 'expired';

  if (!status.is_trial && !isExpired) return null;

  const bgClass = isExpired || isUrgent
    ? 'bg-destructive text-destructive-foreground'
    : 'bg-amber-500 text-white';

  const message = isExpired
    ? 'Your trial has expired. Upgrade now to continue using OptiBuild.'
    : daysLeft === 0
      ? 'Your trial expires today! Upgrade now to avoid interruption.'
      : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left in your free trial.`;

  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium ${bgClass}`}>
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="flex-1">{message}</span>
      <button
        onClick={onUpgradeClick}
        className="flex items-center gap-1.5 rounded-md bg-white/20 hover:bg-white/30 px-3 py-1 text-xs font-semibold transition-colors"
      >
        <Zap className="h-3.5 w-3.5" />
        Upgrade Now
      </button>
      {!isExpired && (
        <button
          onClick={() => setDismissed(true)}
          className="ml-1 rounded p-0.5 opacity-70 hover:opacity-100 transition-opacity"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
